import { useCallback, useEffect, useRef, useState } from 'react';
import { useStreak, useTotalXpQuery } from './hooks.js';
import {
  didLevelUp,
  streakMilestoneCrossed,
  xpToLevel,
  type StreakMilestone,
} from './level.js';
import { track } from '../lib/analytics.js';

/**
 * Level-up + streak-milestone detection, shared by every session player.
 *
 * All three RPCs — `complete_lesson`, `complete_exam_task`,
 * `complete_flashcard_session` — award XP and extend the streak, so all
 * three can cross a 500-XP boundary or a 3/7/14/30/… day milestone. The
 * detection used to live inside LessonPlayer, which meant a learner could
 * hit level 4 on a flashcard session and be told nothing: same crossing,
 * no celebration, purely because of which screen they were on.
 *
 * HOW IT WORKS. The hook snapshots XP + streak when the player mounts (i.e.
 * BEFORE the session is submitted), then watches the React Query cache. The
 * session-completion RPCs write the server's canonical totals into that
 * cache, so the crossing shows up as a plain value change. Each crossing
 * fires at most once per session — the flags below never go back up.
 *
 * `active` is the consumer's "this session is finished" signal. Detection is
 * gated on it so a background refetch mid-session (the pending-queue flusher
 * landing an older completion, say) can't fire a celebration over a lesson
 * the user is still playing.
 */
export interface CelebrationCrossings {
  /** The session crossed a level boundary; show <LevelUpModal> after the
   *  completion celebration's Continue. */
  levelUpPending: boolean;
  /** The milestone the streak just reached, or null. */
  streakMilestone: StreakMilestone | null;
  /** Level to display in the modal (post-crossing). */
  level: number;
  /** Mark the level-up as celebrated — call it when the modal is dismissed. */
  clearLevelUp: () => void;
}

export function useCelebrationCrossings(active: boolean): CelebrationCrossings {
  const { total: totalXpNow, loaded: xpLoaded } = useTotalXpQuery();
  const { data: streakRow } = useStreak();
  const streakNow = streakRow?.current_streak ?? 0;

  const xpBeforeRef = useRef<number | null>(null);
  const baselineReadyRef = useRef(false);
  const streakBeforeRef = useRef<number | null>(null);

  // Seed the pre-session XP baseline ONLY once the XP query has truly
  // resolved — never from the `placeholderData: 0` shown during a cold load.
  // Seeding off 0 made every completion read as a level-1→N jump, re-firing
  // the LevelUpModal on sessions the user had already cleared (bug: re-fired
  // at 742 XP, 2026-06-03). A session takes far longer than the sub-second
  // XP fetch, so by completion the baseline is the genuine pre-session total.
  if (xpBeforeRef.current === null && xpLoaded) {
    xpBeforeRef.current = totalXpNow;
    baselineReadyRef.current = true;
  }
  if (streakBeforeRef.current === null) streakBeforeRef.current = streakNow;

  const [levelUpPending, setLevelUpPending] = useState(false);
  const [streakMilestone, setStreakMilestone] = useState<StreakMilestone | null>(null);
  // "Already detected" lives in refs, not in the state above: `clearLevelUp`
  // flips the state back to false when the modal closes, and a state-based
  // guard would read that as "not detected yet" and celebrate the same
  // crossing a second time (plus a second analytics event).
  const levelUpFiredRef = useRef(false);
  const milestoneFiredRef = useRef(false);

  useEffect(() => {
    if (!active) return;
    const xpBefore = xpBeforeRef.current ?? 0;
    const streakBefore = streakBeforeRef.current ?? 0;
    // Only trust a level-up if the baseline was seeded from loaded XP data.
    // If the query never resolved before completion (extreme cold/offline
    // case), suppress rather than risk a false celebration — missing a rare
    // real level-up is far better than spamming one the user already saw.
    if (baselineReadyRef.current && !levelUpFiredRef.current && didLevelUp(xpBefore, totalXpNow)) {
      levelUpFiredRef.current = true;
      setLevelUpPending(true);
      track('streak_milestone', { kind: 'level_up', level: xpToLevel(totalXpNow) });
    }
    if (!milestoneFiredRef.current) {
      const m = streakMilestoneCrossed(streakBefore, streakNow);
      if (m) {
        milestoneFiredRef.current = true;
        setStreakMilestone(m);
        track('streak_milestone', { kind: 'streak', days: m });
      }
    }
  }, [active, totalXpNow, streakNow]);

  const clearLevelUp = useCallback(() => setLevelUpPending(false), []);

  return {
    levelUpPending,
    streakMilestone,
    level: xpToLevel(totalXpNow),
    clearLevelUp,
  };
}
